import { createApi } from "@reduxjs/toolkit/query/react";
import { customFetchBaseQuery } from "./customFetchBaseQuery";

/**
 * What a message is about. Mirrors INQUIRY_TOPICS on the backend; it decides
 * the subject line Asher sees and how Admin → Inbox labels it.
 */
export type InquiryTopic =
  | "general"
  | "work-with-us"
  | "service"
  | "find-home"
  | "ownership"
  | "premium"
  | "protection"
  | "broken-page"
  | "careers"
  | "press"
  | "privacy"
  | "feedback";

export const INQUIRY_TOPIC_LABELS: Record<InquiryTopic, string> = {
  general: "General question",
  "work-with-us": "Working with SimplerHome",
  service: "Help with a home service",
  "find-home": "I can't find my home",
  ownership: "Home ownership question",
  premium: "SimplerHome Premium",
  protection: "Home Protection",
  "broken-page": "Something isn't working",
  careers: "Careers",
  press: "Press",
  privacy: "Privacy request",
  feedback: "Feedback",
};

export const isInquiryTopic = (v: unknown): v is InquiryTopic =>
  typeof v === "string" && Object.prototype.hasOwnProperty.call(INQUIRY_TOPIC_LABELS, v);

export type ContactFormBody = {
  name: string;
  email: string;
  message: string;
  subject?: string;
  phone?: string;
  topic?: InquiryTopic;
  /** Where they were when they wrote (path + query). */
  pageUrl?: string;
  /** The home it's about, if any (REAPI or Mongo id). */
  propertyId?: string;
  propertyLabel?: string;
  trade?: string;
  /** Honeypot: hidden from people, filled by bots. */
  website?: string;
};

export const apiContactSlice = createApi({
  reducerPath: "api-contact",
  baseQuery: customFetchBaseQuery,
  endpoints: (builder) => {
    return {
      submitContactForm: builder.mutation({
        query: (body: ContactFormBody) => ({
          url: "/api/contact/submit",
          method: "POST",
          body,
        }),
      }),
    };
  },
});

/** The server's message from a failed submit, in either error shape it uses. */
export const contactErrorMessage = (error: any): string => {
  if (error?.status === 429) {
    return error?.data?.error || "You've sent several messages in a short time. Please wait a little and try again.";
  }
  if (error?.status === "FETCH_ERROR") {
    return "We couldn't reach SimplerHome. Check your connection and try again.";
  }
  return (
    error?.data?.errors?.message ||
    error?.data?.error ||
    "We couldn't send your message just now. Please try again, or email info@simplerhome.app."
  );
};

export const { useSubmitContactFormMutation } = apiContactSlice;
